/**
 * FilePicker — file upload with drag-drop, progress bar, and preview
 *
 * @example
 * <FilePicker
 *   accept=".pdf,.doc,.docx"
 *   maxSizeMb={5}
 *   label={t('admin.upload_contract')}
 *   onFile={(file) => setFile(file)}
 *   progress={uploadProgress}
 * />
 */
import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Upload, X, FileText, Image, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];

function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export default function FilePicker({
  label,
  accept,
  maxSizeMb,
  multiple,
  onFile,
  onFiles,
  progress,
  value,
  onClear,
  className,
  disabled,
}) {
  const { t } = useLanguage();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [localFile, setLocalFile] = useState(null);

  const validate = (file) => {
    if (maxSizeMb && file.size > maxSizeMb * 1024 * 1024) {
      return t('admin.file_too_large').replace('{max}', String(maxSizeMb));
    }
    return null;
  };

  const handleFiles = (files) => {
    setError(null);
    const arr = Array.from(files);
    if (!multiple) {
      const file = arr[0];
      if (!file) return;
      const err = validate(file);
      if (err) { setError(err); return; }
      setLocalFile(file);
      if (IMAGE_TYPES.includes(file.type)) {
        const url = URL.createObjectURL(file);
        setPreview(url);
      } else {
        setPreview(null);
      }
      onFile?.(file);
    } else {
      const valid = arr.filter((f) => !validate(f));
      if (valid.length < arr.length) {
        setError(t('admin.file_too_large').replace('{max}', String(maxSizeMb)));
      }
      onFiles?.(valid);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  };

  const handleChange = (e) => {
    if (e.target.files?.length) handleFiles(e.target.files);
    e.target.value = '';
  };

  const clearFile = () => {
    setLocalFile(null);
    setPreview(null);
    setError(null);
    onClear?.();
    onFile?.(null);
  };

  const displayFile = value || localFile;
  const done = progress === 100;

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <p className="text-sm font-bold text-[var(--text-main)] mb-1.5">{label}</p>
      )}

      {displayFile ? (
        // ── File selected preview ───────────────────────────────────────────
        <div className="rounded-xl border-2 border-[var(--border-light)] bg-white p-3">
          <div className="flex items-center gap-3">
            {preview ? (
              <img
                src={preview}
                alt={displayFile.name || t('admin.file_preview')}
                className="w-12 h-12 rounded-lg object-cover border"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-[var(--bg-app)] flex items-center justify-center text-[var(--text-muted)]">
                {IMAGE_TYPES.includes(displayFile.type) ? (
                  <Image size={22} />
                ) : (
                  <FileText size={22} />
                )}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate text-[var(--text-main)]">
                {displayFile.name}
              </p>
              <p className="text-xs text-[var(--text-muted)]">
                {displayFile.size ? formatBytes(displayFile.size) : ''}
              </p>
              {progress != null && !done && (
                <Progress value={progress} className="mt-1.5 h-1.5" />
              )}
              {done && (
                <div className="flex items-center gap-1 mt-1 text-[var(--brand-success)] text-xs font-bold">
                  <CheckCircle size={12} />
                  {t('admin.file_uploaded')}
                </div>
              )}
            </div>
            <button
              onClick={clearFile}
              className="p-1.5 rounded-lg hover:bg-[var(--bg-app)] text-[var(--text-muted)] hover:text-[var(--brand-danger)] transition-colors"
              aria-label={t('admin.file_remove')}
              disabled={disabled}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : (
        // ── Drop zone ───────────────────────────────────────────────────────
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={t('admin.file_drop_zone')}
          className={cn(
            'rounded-xl border-2 border-dashed transition-all cursor-pointer',
            'flex flex-col items-center justify-center gap-2 py-8 px-4 text-center',
            dragging
              ? 'border-[var(--brand-primary)] bg-[var(--brand-primary)]/5'
              : 'border-[var(--border-heavy)] bg-white hover:border-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/5',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
          onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => !disabled && inputRef.current?.click()}
          onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !disabled) inputRef.current?.click(); }}
        >
          <div className="w-10 h-10 rounded-xl bg-[var(--brand-primary)]/10 flex items-center justify-center text-[var(--brand-primary)]">
            <Upload size={20} />
          </div>
          <div>
            <p className="text-sm font-bold text-[var(--text-main)]">
              {t('admin.file_drag_title')}
            </p>
            <p className="text-xs text-[var(--text-muted)] mt-0.5">
              {accept
                ? t('admin.file_accepted').replace('{types}', accept)
                : t('admin.file_any_type')}
              {maxSizeMb && ` — ${t('admin.file_max_size').replace('{max}', String(maxSizeMb))}`}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-xl border-2 font-bold h-8 mt-1"
            type="button"
            disabled={disabled}
          >
            {t('admin.file_browse')}
          </Button>
        </div>
      )}

      {error && (
        <p className="text-xs text-[var(--brand-danger)] font-semibold mt-1" role="alert">
          {error}
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
      />
    </div>
  );
}

FilePicker.propTypes = {
  label: PropTypes.string,
  /** Accepted file types e.g. ".pdf,.docx" */
  accept: PropTypes.string,
  /** Max file size in MB */
  maxSizeMb: PropTypes.number,
  multiple: PropTypes.bool,
  /** Called with selected File (single mode) */
  onFile: PropTypes.func,
  /** Called with File[] (multiple mode) */
  onFiles: PropTypes.func,
  /** Upload progress 0-100 */
  progress: PropTypes.number,
  /** Controlled file value */
  value: PropTypes.object,
  /** Called when file is removed */
  onClear: PropTypes.func,
  className: PropTypes.string,
  disabled: PropTypes.bool,
};

FilePicker.defaultProps = {
  multiple: false,
  disabled: false,
};
